import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  TemplateRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import {
  DataTableComponent,
  TableColumn,
  TableConfig,
} from '@components/data-table/data-table.component';
import { Loading } from '@components/ui/loading/loading';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import { OperationMonitoringService } from '@6grain/masofa-monolith-api-client/common/api/operationMonitoring.service';
import { HostLayoutSnapshot } from '@6grain/masofa-monolith-api-client/common/model/hostLayoutSnapshot';

@Component({
  selector: 'app-orchestration-host-layout-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SecondaryButton,
    DataTableComponent,
    Loading,
    CopyableIdentifierComponent,
    OrchestrationTableHeadingComponent,
  ],
  templateUrl: './orchestration-host-layout-page.html',
  styleUrl: './orchestration-host-layout-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationHostLayoutPageComponent implements OnInit {
  private readonly api = inject(OperationMonitoringService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly displayTemplate = viewChild<TemplateRef<unknown>>('displayTemplate');
  private readonly configKeyTemplate = viewChild<TemplateRef<unknown>>('configKeyTemplate');
  private readonly pathTemplate = viewChild<TemplateRef<unknown>>('pathTemplate');

  readonly loading = signal(false);
  readonly errorMessage = signal('');
  readonly snapshot = signal<HostLayoutSnapshot | null>(null);
  readonly endpointQuery = signal('');
  readonly pathQuery = signal('');

  readonly visibleEndpoints = computed(() =>
    filterByCatalogQuery(this.snapshot()?.endpoints ?? [], this.endpointQuery(), item => [
      item.kind ?? '',
      item.name ?? '',
      item.host ?? '',
      item.display ?? '',
      item.configKey ?? '',
      item.port == null ? '' : String(item.port),
    ]),
  );

  readonly visiblePaths = computed(() =>
    filterByCatalogQuery(this.snapshot()?.paths ?? [], this.pathQuery(), item => [
      item.kind ?? '',
      item.configKey ?? '',
      item.path ?? '',
    ]),
  );

  readonly endpointColumns = computed<TableColumn[]>(() => [
    { field: 'kind', header: 'Kind', type: 'text' },
    { field: 'name', header: 'Name', type: 'text' },
    { field: 'host', header: 'Host', type: 'text' },
    { field: 'port', header: 'Port', type: 'number' },
    {
      field: 'display',
      header: 'Address',
      type: 'custom',
      customTemplate: this.displayTemplate(),
    },
    {
      field: 'configKey',
      header: 'Source',
      type: 'custom',
      customTemplate: this.configKeyTemplate(),
    },
  ]);

  readonly pathColumns = computed<TableColumn[]>(() => [
    { field: 'kind', header: 'Kind', type: 'text' },
    {
      field: 'configKey',
      header: 'Source',
      type: 'custom',
      customTemplate: this.configKeyTemplate(),
    },
    {
      field: 'path',
      header: 'Path',
      type: 'custom',
      customTemplate: this.pathTemplate(),
    },
    { field: 'exists', header: 'HOST_LAYOUT_DIRECTORY_EXISTS', type: 'boolean' },
  ]);

  readonly endpointTableConfig = computed<TableConfig>(() => ({
    paginator: true,
    rows: 100,
    rowsPerPageOptions: [10, 25, 50, 100],
    emptyMessage: this.endpointQuery().trim() ? 'No catalog matches' : 'HOST_LAYOUT_NO_ENDPOINTS',
  }));

  readonly pathTableConfig = computed<TableConfig>(() => ({
    ...this.endpointTableConfig(),
    emptyMessage: this.pathQuery().trim() ? 'No catalog matches' : 'HOST_LAYOUT_NO_PATHS',
  }));

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    if (this.loading()) return;
    this.loading.set(true);
    this.errorMessage.set('');
    this.api.monitoringHostLayoutGet().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: snapshot => {
        this.snapshot.set(snapshot);
        this.loading.set(false);
      },
      error: () => {
        this.snapshot.set(null);
        this.errorMessage.set('HOST_LAYOUT_LOAD_ERROR');
        this.loading.set(false);
      },
    });
  }

  onEndpointQuery(query: string): void {
    this.endpointQuery.set(query ?? '');
  }

  onPathQuery(query: string): void {
    this.pathQuery.set(query ?? '');
  }
}
